import React, { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Alert } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../stores/authStore'
import { isAdminRole } from '@/shared/utils/roleUtils'

interface ProtectedRouteProps {
  children: ReactNode
  requireAuth?: boolean
  requireAdmin?: boolean
  requirePermissions?: string[]
  redirectTo?: string
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
  requirePermissions = [],
  redirectTo = '/login',
}) => {
  const location = useLocation()
  const { isAuthenticated, user } = useAuthStore()
  const { t } = useTranslation('common')

  // 检查权限
  const checkPermissions = (): boolean => {
    if (!user) return false

    if (requireAdmin && !isAdminRole(user.role)) {
      return false
    }

    if (requirePermissions.length > 0) {
      const hasAllPermissions = requirePermissions.every(permission => {
        if (isAdminRole(user.role)) {
          return true
        }
        const userPermissions = ['read', 'write_own', 'view_own_conversations', 'send_messages']
        return userPermissions.includes(permission)
      })
      return hasAllPermissions
    }

    return true
  }

  // 未认证 → 跳转登录（token 过期由 axios 401 拦截器处理）
  if (requireAuth && !isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  // 已认证但权限不足
  if (requireAuth && isAuthenticated && !checkPermissions()) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        padding: '24px'
      }}>
        <Alert
          message={t('errors.insufficientPermissions')}
          description={
            <div>
              <p>{t('errors.noPermissionDescription')}</p>
              <p>{t('errors.requiredPermissions')}</p>
              <ul>
                {requireAdmin && <li>{t('errors.adminRoleRequired')}</li>}
                {requirePermissions.map(permission => (
                  <li key={permission}>{permission}</li>
                ))}
              </ul>
              <p>{t('errors.currentUserRole')}{user?.role}</p>
            </div>
          }
          type="error"
          showIcon
          style={{ maxWidth: '600px' }}
        />
      </div>
    )
  }

  return <>{children}</>
}

export const AuthRequiredRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requireAuth={true}>{children}</ProtectedRoute>
)

export const AdminRequiredRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requireAuth={true} requireAdmin={true}>{children}</ProtectedRoute>
)

export const PublicRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requireAuth={false}>{children}</ProtectedRoute>
)

export { ProtectedRoute }

export default ProtectedRoute